import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Stack,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShipping from "@mui/icons-material/LocalShipping";

import { useTranslation } from "../../../../hooks/useTranslation";
import { OrderDetail } from "../../types";
import { CargoRatesStep } from "./CargoRatesStep";
import { DimensionsAndPriceStep } from "./DimensionAndPriceStep";

type ModalStep = "dimensions-and-price" | "carrier-selection";

export type EditedItem = {
  id: number; // orderItemId
  price: number; // unit price override
  quantity: number; // user-selected qty
};

export type EditedDimensions = {
  length: string;
  width: string;
  height: string;
  weight: string;
};

interface PrepareShipmentModalProps {
  open: boolean;
  onClose: () => void;
  orderDetails: OrderDetail;
}

export const PrepareShipmentModal = ({
  open,
  onClose,
  orderDetails,
}: PrepareShipmentModalProps) => {
  const { translate } = useTranslation("pages.orderDetails.prepareShipment");

  const [currentStep, setCurrentStep] = useState<ModalStep>(
    "dimensions-and-price"
  );

  // Backend expects: shipmentRequest.itemPrices = [{ id, value }]
  const [itemPrices, setItemPrices] = useState<{ id: number; value: number }[]>(
    []
  );
  // Backend expects: shipmentRequest.dimensions = { length, width, height, weight } (numbers)
  const [dimensions, setDimensions] = useState<{
    length: number;
    width: number;
    height: number;
    weight: number;
  } | null>(null);
  // Backend expects: items = [{ productCode, quantity, orderItemId }]
  const [items, setItems] = useState<
    { orderItemId: number; productCode: string; quantity: number }[]
  >([]);

  const handleClose = () => {
    setCurrentStep("dimensions-and-price");
    setItemPrices([]);
    setDimensions(null);
    setItems([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocalShipping fontSize="small" />
          <Typography variant="h6">{translate("title")}</Typography>
          <Box flexGrow={1} />
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary" mt={1}>
          {currentStep === "dimensions-and-price" &&
            translate("priceEditDescription")}
          {currentStep === "carrier-selection" &&
            translate("carrierDescription")}
        </Typography>
      </DialogTitle>

      {currentStep === "dimensions-and-price" && (
        <DimensionsAndPriceStep
          orderDetails={orderDetails}
          onClose={handleClose}
          onNext={(editedItems, editedDimensions) => {
            // 1) itemPrices
            setItemPrices(
              editedItems.map((it) => ({
                id: it.id,
                value: it.price,
              }))
            );

            // 2) dimensions (string -> number)
            setDimensions({
              length: Number(editedDimensions.length),
              width: Number(editedDimensions.width),
              height: Number(editedDimensions.height),
              weight: Number(editedDimensions.weight),
            });

            // 3) items payload for shipment create
            // productCode is on orderDetails.orderItems[*].productCode
            setItems(
              editedItems.map((it) => {
                const productCode =
                  orderDetails.orderItems.find((oi) => oi.id === it.id)
                    ?.productCode ?? "";
                return {
                  orderItemId: it.id,
                  productCode,
                  quantity: it.quantity,
                };
              })
            );

            setCurrentStep("carrier-selection");
          }}
        />
      )}

      {currentStep === "carrier-selection" && dimensions && (
        <CargoRatesStep
          orderDetails={orderDetails}
          orderBarcode={orderDetails.barcode}
          itemPrices={itemPrices}
          dimensions={dimensions}
          items={items}
          onBack={() => setCurrentStep("dimensions-and-price")}
          onClose={handleClose}
        />
      )}
    </Dialog>
  );
};
